import axios from 'axios';
import qs from 'qs';
import {stopTokenRefreshInterval, startTokenRefreshInterval, clearAuthState} from './utils/tokenRefresh';
import Cookies from 'js-cookie';

// Enable sending cookies with requests (for CORS)
axios.defaults.withCredentials = true;

// Basic auth credentials for OAuth endpoint
const OAUTH_CLIENT_ID = 'EoYCm7LFks0aSI5RCMiNjCvOEehG5EEC';
const OAUTH_CLIENT_SECRET = 'YaFAERl0fJX2Pfl7Qd6m';

// Encode credentials for Basic Auth header
const getBasicAuthHeader = () => {
  const credentials = `${OAUTH_CLIENT_ID}:${OAUTH_CLIENT_SECRET}`;
  const encoded = btoa(credentials); // Use btoa for browser-compatible base64 encoding
  return `Basic ${encoded}`;
};

// Parse JWT to get expiry
const parseJwt = (token: string) => {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (e) {
    return null;
  }
};

const getExpiry = (token: string) => {
  if (token == null) return new Date();
  const payload = parseJwt(token);
  return payload?.exp ? new Date(payload.exp * 1000) : new Date();
};

/**
 * The OAuth token endpoint's response body, for the grant types used below.
 * Both fields are read unguarded when storing the session, so both are required here.
 */
interface OAuthTokens {
  access_token: string;
  refresh_token: string;
}

// Get cookie domain from environment
const getCookieDomain = () => {
  return import.meta.env.VITE_COOKIE_DOMAIN || 'localhost';
};

export default class Api {
  static loginToServer(
    username: string,
    password: string,
    // Note the argument each callback receives: loginToServer reports the error *message*,
    // while selectOrganisation below passes the error object itself.
    onError?: (message: string) => void,
    onDone?: (tokens: OAuthTokens) => void,
  ) {
    axios
      .post(`/oauth/token`, qs.stringify({grant_type: 'admin', username: username, password: password}), {
        headers: {
          Authorization: getBasicAuthHeader(),
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        withCredentials: true, // Enable cookies
      })
      .then((response) => {
        let data = response.data;
        // Store tokens in localStorage
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('refresh_token', data.refresh_token);
        // Store username for auto-fill
        localStorage.setItem('last_username', username);

        // Store tokens in cookies for WebSocket authentication (same as desktop)
        Cookies.set('main_token', data.access_token, {
          path: '/',
          domain: getCookieDomain(),
          expires: getExpiry(data.access_token),
        });
        Cookies.set('main_refresh_token', data.refresh_token, {
          path: '/',
          domain: getCookieDomain(),
          expires: getExpiry(data.refresh_token),
        });

        // Also set Authorization header for backward compatibility with HTTP requests
        axios.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`;
        console.log('Login successful, JWT stored in localStorage and cookies (main_token, main_refresh_token)');
        // Restart token refresh interval
        startTokenRefreshInterval();
        onDone && onDone(data);
      })
      .catch((error) => {
        console.log(`Got error ${error}`);
        onError && onError(error.message);
        console.log(onError);
      });
  }

  static selectOrganisation(
    orgId: string,
    onDone?: (tokens: OAuthTokens) => void,
    onError?: (error: unknown) => void,
  ) {
    // Get refresh token from localStorage
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      console.error('No refresh token found');
      onError && onError(new Error('No refresh token'));
      return;
    }

    axios
      .post(`/oauth/token`, qs.stringify({grant_type: 'refresh_token', refresh_token: refreshToken, organisation_id: orgId}), {
        headers: {
          Authorization: getBasicAuthHeader(),
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        withCredentials: true,
      })
      .then((response) => {
        if (response.status === 200 && response.data && response.data.access_token) {
          const data = response.data;
          // Update localStorage with new token
          localStorage.setItem('access_token', data.access_token);
          if (data.refresh_token) {
            localStorage.setItem('refresh_token', data.refresh_token);
          }
          // Update cookies
          Cookies.set('main_token', data.access_token, {
            path: '/',
            domain: getCookieDomain(),
            expires: getExpiry(data.access_token),
          });
          if (data.refresh_token) {
            Cookies.set('main_refresh_token', data.refresh_token, {
              path: '/',
              domain: getCookieDomain(),
              expires: getExpiry(data.refresh_token),
            });
          }
          // Update Authorization header
          axios.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`;
          // Store selected organisation ID for token refresh
          localStorage.setItem('selected_organisation_id', orgId);
          console.log('Organization selected, new JWT stored in localStorage and cookies');
          onDone && onDone(data);
        }
      })
      .catch((error) => {
        console.error('Failed to select organization:', error);
        onError && onError(error);
      });
  }

  static logout() {
    stopTokenRefreshInterval();
    clearAuthState();
    console.log('Logged out, tokens and cookies cleared');
  }

  static initializeAuth() {
    // Restore token from localStorage on app startup
    const token = localStorage.getItem('access_token');
    const refreshToken = localStorage.getItem('refresh_token');

    if (token) {
      // Set cookies to match localStorage
      Cookies.set('main_token', token, {
        path: '/',
        domain: getCookieDomain(),
        expires: getExpiry(token),
      });
      if (refreshToken) {
        Cookies.set('main_refresh_token', refreshToken, {
          path: '/',
          domain: getCookieDomain(),
          expires: getExpiry(refreshToken),
        });
      }
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }
}
